import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PaperAirplaneIcon as PaperAirplaneSolid } from '@heroicons/react/24/solid';
import {
  UserIcon,
  Cog6ToothIcon,
  ArrowUturnLeftIcon,
  ClipboardDocumentIcon,
  PencilIcon,
  ArrowPathIcon,
  PhotoIcon,
  XMarkIcon,
  EyeIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';
import MobileLayout from '../components/MobileLayout';
import { TokenBalance } from '../components/TokenBalance';
import { ThreadSidebar } from '../components/ThreadSidebar';
import { usePusher } from '../contexts/PusherContext';
import { useAuth } from '../contexts/AuthContext';
// import { ChatInstallPrompt } from '../components/PWA';
import { trackChatInteraction } from '../hooks/usePWA';
import axios from 'axios';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';
import { useTranslation } from 'react-i18next';
import ContextInfoPanel from '../components/ContextInfoPanel';
import TarotPullPanel from '../components/TarotPullPanel';
import ConversationDashboard from '../components/ConversationDashboard';
import RitualPhrasePanel from '../components/RitualPhrasePanel';
import SoftWallModal from '../components/SoftWallModal';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  createdAt: string;
  conversationId?: string;
  threadId?: string;
  flaggedByModeration?: boolean;
  model?: string;
  imageUrl?: string;
}

interface Conversation {
  id: string;
  title: string;
  companion?: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  lastMessageAt?: string;
  messageCount: number;
}

const MAX_MESSAGE_LENGTH = 20000;

const Chat: React.FC = () => {
  const { companionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscribe, unsubscribe, isConnected } = usePusher();
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [balanceRefreshTrigger, setBalanceRefreshTrigger] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false); // Start with false until we know
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [noCompanion, setNoCompanion] = useState(false);
  const [rollbackMessageId, setRollbackMessageId] = useState<string | null>(null);
  const [softWall, setSoftWall] = useState<{
    type: 'daily_limit' | 'budget_warning';
    usage: { used: number; limit: number };
  } | null>(null);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [hoveredUserMessageId, setHoveredUserMessageId] = useState<string | null>(null);
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [showTarotPanel, setShowTarotPanel] = useState(false);
  const [showConversationDashboard, setShowConversationDashboard] = useState(false);
  const [showRitualPhrasePanel, setShowRitualPhrasePanel] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<any>(null);
  const loadingMoreRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);
  const firstAssistantMessageRef = useRef(false);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);

  // Load or create conversation
  useEffect(() => {
    const initializeChat = async () => {
      try {
        setIsLoadingMessages(true);

        let activeConversation;

        if (companionId) {
          // If companionId is provided, get or create conversation for that specific companion
          const conversationRes = await axios.get(`/chat/conversations/companion/${companionId}`);
          activeConversation = conversationRes.data;
        } else {
          // Otherwise, get the most recent conversation
          const conversationsRes = await axios.get('/chat/conversations');
          activeConversation = conversationsRes.data[0]; // Get most recent

          // If no conversation exists, create one
          if (!activeConversation) {
            const newConvRes = await axios.post('/chat/conversations');
            activeConversation = newConvRes.data;
          }
        }

        setConversation(activeConversation);

        // Load threads and initial messages if conversation exists
        if (activeConversation.id) {
          // Load threads first
          const threadsRes = await axios.get(
            `/chat/conversations/${activeConversation.id}/threads`,
          );
          const threads = threadsRes.data || [];

          // Set active thread (default to General or first thread)
          let initialThreadId: string | null = null;
          if (threads.length > 0) {
            const generalThread = threads.find((t: any) => t.title === 'General');
            initialThreadId = generalThread?.id || threads[0].id;
            setActiveThreadId(initialThreadId);
          }

          // Load messages for the active thread
          if (initialThreadId) {
            const messagesRes = await axios.get(
              `/chat/threads/${initialThreadId}/messages?limit=30`,
            );
            setMessages(messagesRes.data.messages || []);
            setHasMoreMessages(messagesRes.data.hasMore || false);
          } else {
            // Fallback: load conversation messages (legacy)
            const messagesRes = await axios.get(
              `/chat/conversations/${activeConversation.id}/messages?limit=30`,
            );
            setMessages(messagesRes.data.messages || []);
            setHasMoreMessages(messagesRes.data.hasMore || false);
          }

          // Mark initial load as complete after a short delay to ensure DOM is ready
          setTimeout(() => {
            initialLoadCompleteRef.current = true;
          }, 500);
        }
      } catch (error: any) {
        logger.error('Failed to initialize chat:', error);
        // Check if the error is because there's no companion
        if (error.response?.status === 404) {
          setNoCompanion(true);
        } else {
          toast.error(t('chat.failedToLoad', 'Failed to load chat'));
        }
      } finally {
        setIsLoadingMessages(false);
      }
    };

    if (user) {
      initializeChat();
    }
  }, [user, companionId]);

  // Helper to set typing indicator with timeout
  const setTypingWithTimeout = useCallback((typing: boolean, timeoutMs: number = 10000) => {
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    setIsTyping(typing);

    // If setting typing to true, set a timeout to clear it
    if (typing) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        typingTimeoutRef.current = null;
      }, timeoutMs);
    }
  }, []);

  // Subscribe to Pusher channel for real-time messages
  useEffect(() => {
    if (!user) {
      setIsSubscribed(false);
      return;
    }

    // Wait a bit if Pusher is not connected yet
    if (!isConnected) {
      logger.log('[Chat] Waiting for Pusher connection...');
      setIsSubscribed(false);
      const retryTimeout = setTimeout(() => {
        logger.log('[Chat] Retrying Pusher subscription...');
      }, 500);
      return () => clearTimeout(retryTimeout);
    }

    const channelName = `private-chat-${user.id}`;
    logger.log('[Chat] Pusher connected, subscribing to channel:', channelName);

    // Unsubscribe from previous channel if exists
    if (channelRef.current) {
      channelRef.current.unbind_all();
      unsubscribe(channelName);
      channelRef.current = null;
    }

    const channel = subscribe(channelName);
    channelRef.current = channel;

    if (channel) {
      // Listen for subscription success
      channel.bind('pusher:subscription_succeeded', () => {
        logger.log('[Chat] Successfully subscribed to channel');
        setIsSubscribed(true);
      });

      // Listen for subscription error
      channel.bind('pusher:subscription_error', (error: any) => {
        logger.error('[Chat] Subscription error:', error);
        setIsSubscribed(false);
      });

      // Listen for new messages
      const handleNewMessage = (data: Message) => {
        logger.log('[Chat] Received new message:', data.role, data.content?.substring(0, 50));

        setMessages((prev) => {
          // Check if message already exists (avoid duplicates)
          const exists = prev.some((msg) => msg.id === data.id);
          if (exists) {
            return prev;
          }

          // If it's a user message, replace the temp message
          if (data.role === 'user') {
            const filtered = prev.filter((msg) => !msg.id.startsWith('temp-'));
            // Check again after filtering
            const stillExists = filtered.some((msg) => msg.id === data.id);
            if (stillExists) {
              return filtered;
            }
            return [...filtered, data];
          }

          // Add assistant message
          return [...prev, data];
        });

        // Clear typing indicator and refresh balance when assistant message arrives
        if (data.role === 'assistant') {
          setTypingWithTimeout(false);
          // Trigger balance refresh
          setBalanceRefreshTrigger(Date.now());

          // Show install prompt after first assistant message
          if (!firstAssistantMessageRef.current) {
            firstAssistantMessageRef.current = true;
            setShowInstallPrompt(true);
          }
        }
      };

      channel.bind('new-message', handleNewMessage);

      // Listen for typing events from backend
      const handleTyping = (data: { isTyping: boolean }) => {
        logger.log('[Chat] Typing event:', data.isTyping);
        // Use timeout to prevent stuck typing indicators
        setTypingWithTimeout(data.isTyping, 15000);
      };

      channel.bind('typing', handleTyping);

      // Listen for conversation updates
      const handleConversationUpdate = (data: Conversation) => {
        logger.log('[Chat] Conversation update:', data.id);
        setConversation(data);
      };

      channel.bind('conversation-update', handleConversationUpdate);

      // Listen for message rollback events
      const handleMessagesRollback = (data: {
        threadId: string;
        rollbackToMessageId: string;
        deletedMessageIds?: string[];
        fullRollback?: boolean;
      }) => {
        logger.log('[Chat] Messages rollback event:', data);
        if (data.fullRollback) {
          // Reload thread messages entirely
          if (data.threadId === activeThreadId) {
            axios
              .get(`/chat/threads/${data.threadId}/messages?limit=30`)
              .then((res) => {
                setMessages(res.data.messages || []);
                setHasMoreMessages(res.data.hasMore || false);
              })
              .catch((err) => logger.error('Failed to reload messages after rollback:', err));
          }
        } else if (data.deletedMessageIds) {
          setMessages((prev) => prev.filter((msg) => !data.deletedMessageIds!.includes(msg.id)));
        }
      };

      channel.bind('messages-rollback', handleMessagesRollback);

      // Listen for individual message deletion (regenerate)
      const handleMessageDeleted = (data: { messageId: string; threadId: string }) => {
        logger.log('[Chat] Message deleted event:', data);
        setMessages((prev) => prev.filter((m) => m.id !== data.messageId));
      };
      channel.bind('message-deleted', handleMessageDeleted);

      // Listen for conversation deletion
      const handleConversationDeleted = (data: { id: string }) => {
        logger.log('[Chat] Conversation deleted event:', data);
        if (data.id === conversation?.id) {
          toast.success(t('chat.conversationDeleted', 'Conversation deleted'));
          navigate('/companions');
        }
      };

      channel.bind('conversation-deleted', handleConversationDeleted);

      // Force check subscription state multiple times
      let checkAttempts = 0;
      const checkInterval = setInterval(() => {
        checkAttempts++;
        if (channel.subscribed) {
          logger.log('[Chat] Channel confirmed subscribed after', checkAttempts, 'attempts');
          setIsSubscribed(true);
          clearInterval(checkInterval);
        } else if (checkAttempts >= 10) {
          logger.error('[Chat] Channel subscription failed after 10 attempts');
          clearInterval(checkInterval);
          // Try to resubscribe
          const newChannel = subscribe(channelName);
          if (newChannel) {
            channelRef.current = newChannel;
            logger.log('[Chat] Attempting resubscription...');
          }
        } else {
          logger.log('[Chat] Channel not yet subscribed, attempt', checkAttempts);
        }
      }, 500);

      return () => {
        logger.log('[Chat] Cleaning up channel subscription');
        clearInterval(checkInterval);
        if (channelRef.current) {
          channelRef.current.unbind_all();
          unsubscribe(channelName);
          channelRef.current = null;
        }
        setIsSubscribed(false);
      };
    }

    return () => {
      logger.log('[Chat] Cleaning up - no channel');
      setIsSubscribed(false);
    };
  }, [user, isConnected, subscribe, unsubscribe, setTypingWithTimeout]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load more messages when scrolling to top
  const loadMoreMessages = async () => {
    // Use ref to prevent multiple simultaneous calls
    if (loadingMoreRef.current) {
      logger.log('[Chat] Already loading more messages, skipping...');
      return;
    }

    if (!hasMoreMessages || messages.length === 0) {
      logger.log('[Chat] Cannot load more:', {
        hasMoreMessages,
        messageCount: messages.length,
      });
      return;
    }

    logger.log('[Chat] Loading more messages...');
    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    const oldestMessage = messages[0];

    try {
      // Use thread-based endpoint if we have an active thread
      const endpoint = activeThreadId
        ? `/chat/threads/${activeThreadId}/messages?limit=30&before=${oldestMessage.id}`
        : `/chat/conversations/${conversation?.id}/messages?limit=30&before=${oldestMessage.id}`;
      const response = await axios.get(endpoint);

      const newMessages = response.data.messages || [];
      const hasMore = response.data.hasMore || false;

      if (newMessages.length > 0) {
        // Prepend new messages to the beginning, avoiding duplicates
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const uniqueNewMessages = newMessages.filter((m: Message) => !existingIds.has(m.id));
          return [...uniqueNewMessages, ...prev];
        });
        setHasMoreMessages(hasMore);

        // Maintain scroll position
        if (messagesContainerRef.current) {
          const previousScrollHeight = messagesContainerRef.current.scrollHeight;
          requestAnimationFrame(() => {
            if (messagesContainerRef.current) {
              const newScrollHeight = messagesContainerRef.current.scrollHeight;
              const scrollDiff = newScrollHeight - previousScrollHeight;
              messagesContainerRef.current.scrollTop += scrollDiff;
            }
          });
        }
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      logger.error('Failed to load more messages:', error);
      toast.error(t('chat.failedToLoadOlder', 'Failed to load older messages'));
    } finally {
      setIsLoadingMore(false);
      loadingMoreRef.current = false;
    }
  };

  // Handle scroll events for infinite scroll
  const handleScroll = () => {
    if (!messagesContainerRef.current || loadingMoreRef.current) return;

    // Don't trigger during initial load
    if (!initialLoadCompleteRef.current) return;

    const { scrollTop } = messagesContainerRef.current;

    // Load more messages when scrolled to top (within 100px)
    if (scrollTop < 100 && hasMoreMessages && messages.length > 0) {
      logger.log('[Chat] Scroll threshold reached, loading more messages...');
      loadMoreMessages();
    }
  };

  // Handle thread selection
  const handleThreadSelect = async (threadId: string) => {
    if (threadId === activeThreadId) return;

    setActiveThreadId(threadId);
    setMessages([]);
    setIsLoadingMessages(true);
    setHasMoreMessages(false);
    initialLoadCompleteRef.current = false;

    try {
      const messagesRes = await axios.get(`/chat/threads/${threadId}/messages?limit=30`);
      setMessages(messagesRes.data.messages || []);
      setHasMoreMessages(messagesRes.data.hasMore || false);
      setTimeout(() => {
        initialLoadCompleteRef.current = true;
      }, 500);
    } catch (error) {
      logger.error('Failed to load thread messages:', error);
      toast.error(t('chat.failedToLoad', 'Failed to load chat'));
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleConversationDeleted = useCallback(() => {
    toast.success(t('chat.conversationDeleted', 'Conversation deleted'));
    navigate('/companions');
  }, [navigate, t]);

  const dismissRollbackMenu = () => {
    setRollbackMessageId(null);
    setShowRollbackConfirm(false);
  };

  const executeRollback = async () => {
    if (!rollbackMessageId || !activeThreadId) return;

    setIsRollingBack(true);
    try {
      const response = await axios.post(`/chat/threads/${activeThreadId}/rollback`, {
        messageId: rollbackMessageId,
      });
      toast.success(t('chat.rollback.success', 'Conversation rolled back successfully'));
      // Filter out deleted messages using IDs from backend response
      const deletedIds: string[] = response.data.deletedMessageIds || [];
      if (deletedIds.length > 0) {
        setMessages((prev) => prev.filter((m) => !deletedIds.includes(m.id)));
      }
      logger.log('[Chat] Rolled back', response.data.deletedCount, 'messages');
    } catch (error: any) {
      logger.error('Failed to roll back:', error);
      const message =
        error.response?.data?.message ||
        t('chat.rollback.error', 'Failed to roll back conversation');
      toast.error(message);
    } finally {
      setIsRollingBack(false);
      setRollbackMessageId(null);
      setShowRollbackConfirm(false);
    }
  };

  const handleStartEdit = (message: Message) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleSubmitEdit = async () => {
    if (!editContent.trim() || !editingMessageId || !activeThreadId || isSending) return;

    setIsSending(true);
    try {
      // Find the message before the one being edited
      const editIdx = messages.findIndex((m) => m.id === editingMessageId);
      const messageBeforeEdit = editIdx > 0 ? messages[editIdx - 1] : null;

      if (messageBeforeEdit) {
        // Rollback to the message before the edited one (deletes edited msg + everything after)
        await axios.post(`/chat/threads/${activeThreadId}/rollback`, {
          messageId: messageBeforeEdit.id,
        });
      }

      // Update UI: remove edited message and everything after, add temp edited message
      const tempMessage: Message = {
        id: 'temp-' + Date.now(),
        content: editContent,
        role: 'user',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === editingMessageId);
        const kept = idx >= 0 ? prev.slice(0, idx) : prev;
        return [...kept, tempMessage];
      });

      setTypingWithTimeout(true, 15000);

      // Send the edited content as a new message
      await axios.post('/chat/send', {
        content: editContent,
        conversationId: conversation?.id,
        threadId: activeThreadId,
      });

      setEditingMessageId(null);
      setEditContent('');
    } catch (error: any) {
      logger.error('Failed to edit message:', error);
      toast.error(t('chat.edit.error', 'Failed to edit message. Please try again.'));
      // Reload messages to get back to consistent state
      if (activeThreadId) {
        try {
          const response = await axios.get(`/chat/threads/${activeThreadId}/messages?limit=50`);
          setMessages(response.data.messages || []);
        } catch {}
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleRitualReset = async () => {
    if (!conversation?.id || isResetting) return;
    setIsResetting(true);
    try {
      await axios.post(`/chat/conversations/${conversation.id}/ritual-reset`);
    } catch (err) {
      console.error('Ritual reset failed:', err);
    } finally {
      setIsResetting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!activeThreadId || isSending || isRegenerating) return;

    setIsRegenerating(true);
    setTypingWithTimeout(true, 15000);

    try {
      // Remove the last AI message from UI immediately
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1].role === 'assistant') {
          return prev.slice(0, -1);
        }
        return prev;
      });

      await axios.post(`/chat/threads/${activeThreadId}/regenerate`);
      // New message will come via Pusher
    } catch (error: any) {
      logger.error('Failed to regenerate:', error);
      toast.error(t('chat.regenerate.error', 'Failed to regenerate response. Please try again.'));
      // Reload messages
      if (activeThreadId) {
        try {
          const response = await axios.get(`/chat/threads/${activeThreadId}/messages?limit=50`);
          setMessages(response.data.messages || []);
        } catch {}
      }
      setTypingWithTimeout(false);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('chat.image.invalidType', 'Please select a JPEG, PNG, GIF, or WebP image.'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('chat.image.tooLarge', 'Image must be under 10MB.'));
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));

    // Reset file input
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
  };

  const handleSendWithImage = async () => {
    if (!selectedImage || isSending) return;

    setIsSending(true);
    setTypingWithTimeout(true, 30000);

    const tempMessage: Message = {
      id: 'temp-' + Date.now(),
      content: inputMessage || '[Image]',
      role: 'user',
      createdAt: new Date().toISOString(),
      imageUrl: imagePreview || undefined,
    };
    setMessages((prev) => [...prev, tempMessage]);

    const messageContent = inputMessage;
    setInputMessage('');
    handleClearImage();

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      if (messageContent) formData.append('content', messageContent);
      if (conversation?.id) formData.append('conversationId', conversation.id);
      if (companionId) formData.append('companionId', companionId);
      if (activeThreadId) formData.append('threadId', activeThreadId);

      await axios.post('/chat/send-with-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error: any) {
      logger.error('Failed to send image:', error);
      setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
      setInputMessage(messageContent);
      setTypingWithTimeout(false);

      const errCode = error.response?.data?.code;
      const errData = error.response?.data;
      if (errCode === 'DAILY_LIMIT_REACHED') {
        setSoftWall({
          type: 'daily_limit',
          usage: { used: errData.dailyMessagesUsed, limit: errData.dailyMessageLimit },
        });
      } else if (errCode === 'MONTHLY_BUDGET_EXCEEDED') {
        setSoftWall({
          type: 'budget_warning',
          usage: { used: errData.monthlyTokensUsed, limit: errData.monthlyTokenBudget },
        });
      } else {
        toast.error(t('chat.image.sendFailed', 'Failed to send image. Please try again.'));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    // If there's a selected image, send with image
    if (selectedImage) {
      handleSendWithImage();
      return;
    }

    if (!inputMessage.trim() || isSending) return;

    if (inputMessage.length > MAX_MESSAGE_LENGTH) {
      toast.error(
        t('chat.messageTooLong', 'Message is too long. Please keep it under {{max}} characters.', {
          max: MAX_MESSAGE_LENGTH.toLocaleString(),
        }),
      );
      return;
    }

    // Track chat interaction for PWA install prompt
    trackChatInteraction();

    const messageContent = inputMessage;
    setInputMessage('');
    setIsSending(true);
    setTypingWithTimeout(true, 15000); // Show typing indicator for AI response with timeout

    // Add user message to UI immediately for better UX
    const tempUserMessage: Message = {
      id: 'temp-' + Date.now(),
      content: messageContent,
      role: 'user',
      createdAt: new Date().toISOString(),
      conversationId: conversation?.id || '',
      threadId: activeThreadId || undefined,
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      // Send message to backend
      await axios.post('/chat/send', {
        content: messageContent,
        conversationId: conversation?.id,
        companionId: companionId, // Include companionId for new conversations
        threadId: activeThreadId, // Include threadId
      });

      // The real message will be received via Pusher and replace the temp one
    } catch (error: any) {
      logger.error('Failed to send message:', error);

      // Remove the temp message and restore input
      setMessages((prev) => prev.filter((msg) => msg.id !== tempUserMessage.id));
      setInputMessage(messageContent);
      setTypingWithTimeout(false);

      const errCode = error.response?.data?.code;
      const errData = error.response?.data;

      if (errCode === 'DAILY_LIMIT_REACHED') {
        setSoftWall({
          type: 'daily_limit',
          usage: { used: errData.dailyMessagesUsed, limit: errData.dailyMessageLimit },
        });
      } else if (errCode === 'MONTHLY_BUDGET_EXCEEDED') {
        setSoftWall({
          type: 'budget_warning',
          usage: { used: errData.monthlyTokensUsed, limit: errData.monthlyTokenBudget },
        });
      } else if (error.response?.status === 400 && errData?.message?.includes('tokens')) {
        toast.error(
          t(
            'chat.insufficientTokens',
            'Insufficient tokens. Please purchase more to continue chatting.',
          ),
        );
      } else if (
        error.response?.status === 413 ||
        (error.response?.status === 400 && errData?.message?.includes('long'))
      ) {
        toast.error(
          t(
            'chat.messageTooLong',
            'Message is too long. Please keep it under {{max}} characters.',
            {
              max: MAX_MESSAGE_LENGTH.toLocaleString(),
            },
          ),
        );
      } else {
        toast.error(t('chat.failedToSend', 'Failed to send message. Please try again.'));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyToInput = useCallback(
    (content: string) => {
      setInputMessage(content);
      toast.success(t('chat.copiedToInput', 'Copied to input'));
    },
    [t],
  );

  const companion = conversation?.companion || {
    name: t('chat.aiCompanion', 'AI Companion'),
    avatarUrl: undefined,
  };

  // Validate avatarUrl to prevent invalid image sources
  if (
    companion.avatarUrl &&
    !companion.avatarUrl.startsWith('http') &&
    !companion.avatarUrl.startsWith('data:')
  ) {
    companion.avatarUrl = undefined;
  }

  const headerAction = (
    <button
      onClick={() => navigate('/profile')}
      className="p-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-dark-text dark:hover:text-dark-text hover:text-light-text transition-colors"
    >
      <UserIcon className="w-6 h-6" />
    </button>
  );

  // Show no companion state
  if (noCompanion) {
    return (
      <MobileLayout headerTitle="" headerAction={headerAction}>
        <div className="h-full flex items-center justify-center bg-dark-bg dark:bg-dark-bg bg-light-bg -mx-4 px-4">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
              <span className="text-4xl">💬</span>
            </div>
            <h2 className="text-xl font-semibold text-dark-text dark:text-dark-text text-light-text mb-2">
              {t('chat.noCompanion.title', 'No Companion Yet')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-6">
              {t(
                'chat.noCompanion.description',
                'Create your AI companion to start chatting. Give them a name, personality, and watch them come to life!',
              )}
            </p>
            <button
              onClick={() => navigate('/companion/name')}
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              <span className="text-lg">✨</span>
              {t('chat.noCompanion.createButton', 'Create Companion')}
            </button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout headerTitle="" headerAction={headerAction}>
      <div className="h-full flex bg-dark-bg dark:bg-dark-bg bg-light-bg -mx-4">
        {/* Thread Sidebar - always visible, collapsed by default */}
        <ThreadSidebar
          conversationId={conversation?.id || null}
          activeThreadId={activeThreadId}
          onThreadSelect={handleThreadSelect}
          onConversationDeleted={handleConversationDeleted}
        />

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat header with companion info */}
          <div
            className="px-4 pt-4 pb-3"
            style={{
              background: 'var(--ds-bg-card, rgba(26,26,26,0.8))',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderBottom: '1px solid var(--ds-border, #2a2a2a)',
            }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                {companion?.avatarUrl ? (
                  <img
                    src={companion.avatarUrl}
                    alt={companion.name}
                    className="w-full h-full rounded-full"
                  />
                ) : (
                  <span className="text-xl">🌈</span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-dark-text dark:text-dark-text text-light-text font-medium">
                    {companion?.name}
                  </h2>
                  {companion && 'id' in companion && companion.id && (
                    <>
                      <button
                        onClick={() => setShowConversationDashboard(true)}
                        className="p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-500 transition-colors"
                        aria-label={t('conversations.title', 'Conversations')}
                        title={t('conversations.title', 'Conversations')}
                      >
                        <span className="text-base">💬</span>
                      </button>
                      <button
                        onClick={() => setShowTarotPanel(true)}
                        className="p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-500 transition-colors"
                        aria-label={t('tarot.title', 'Tarot Pull')}
                        title={t('tarot.title', 'Tarot Pull')}
                      >
                        <span className="text-base">🔮</span>
                      </button>
                      <button
                        onClick={() => setShowContextPanel(true)}
                        className="p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-500 transition-colors"
                        aria-label={t('context.title', 'Active Context')}
                        title={t('context.title', 'Active Context')}
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleRitualReset}
                        disabled={isResetting}
                        className={`p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-500 transition-colors ${isResetting ? 'animate-spin' : ''}`}
                        aria-label={t('ritualReset.title', 'Ritual Reset')}
                        title={t('ritualReset.title', 'Ritual Reset')}
                      >
                        <ArrowPathRoundedSquareIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/settings/companion/${companion.id}`)}
                        className="p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-dark-text dark:hover:text-dark-text hover:text-light-text transition-colors"
                        aria-label="Companion settings"
                      >
                        <Cog6ToothIcon className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
                <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                  {isConnected
                    ? t('chat.activeNow', 'Active now')
                    : t('chat.connecting', 'Connecting...')}
                </p>
              </div>
              <TokenBalance refreshTrigger={balanceRefreshTrigger} />
            </div>
          </div>

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4"
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {isLoadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                    {t('chat.loadingMessages', 'Loading messages...')}
                  </p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
                    {t('chat.initialGreeting1', "Hey there! I'm so excited to finally meet you!")}
                  </p>
                  <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                    {t('chat.initialGreeting2', 'How are you feeling today?')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="flex justify-center py-2 mb-2">
                    <div className="flex items-center space-x-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-dark-text-secondary dark:bg-dark-text-secondary bg-light-text-secondary rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-dark-text-secondary dark:bg-dark-text-secondary bg-light-text-secondary rounded-full animate-bounce"
                          style={{ animationDelay: '0.1s' }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-dark-text-secondary dark:bg-dark-text-secondary bg-light-text-secondary rounded-full animate-bounce"
                          style={{ animationDelay: '0.2s' }}
                        ></div>
                      </div>
                      <span>{t('chat.loadingOlderMessages', 'Loading older messages...')}</span>
                    </div>
                  </div>
                )}

                {/* Show indicator based on message state */}
                {!isLoadingMore && messages.length > 0 && (
                  <div className="text-center py-3 mb-2">
                    {hasMoreMessages ? (
                      <button
                        onClick={loadMoreMessages}
                        className="text-xs text-primary-500 hover:text-primary-400 transition-colors"
                      >
                        {t('chat.loadOlderMessages', 'Load older messages')}
                      </button>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-12 h-[1px] bg-dark-border dark:bg-dark-border bg-light-border"></div>
                        <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary px-2">
                          {t('chat.beginningOfConversation', 'Beginning of conversation')}
                        </p>
                        <div className="w-12 h-[1px] bg-dark-border dark:bg-dark-border bg-light-border"></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Date separator */}
                <div className="flex items-center justify-center my-4">
                  <span className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary bg-dark-card dark:bg-dark-card bg-light-surface px-3 py-1 rounded-full">
                    {t('chat.today', 'Today')}
                  </span>
                </div>

                {messages.map((message) => (
                  <div
                    key={message.id}
                    className="relative group"
                    onMouseEnter={() =>
                      !isSending &&
                      !message.id.startsWith('temp-') &&
                      setHoveredMessageId(message.id)
                    }
                    onMouseLeave={() =>
                      hoveredMessageId === message.id && setHoveredMessageId(null)
                    }
                  >
                    <div
                      className={`flex items-center gap-1 ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      } mb-1`}
                    >
                      {/* Action buttons - left of user messages */}
                      {message.role === 'user' &&
                        hoveredMessageId === message.id &&
                        !showRollbackConfirm &&
                        !editingMessageId && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleStartEdit(message)}
                              className="flex-shrink-0 p-1.5 rounded-full bg-dark-card dark:bg-dark-card bg-light-surface border border-dark-border dark:border-dark-border border-light-border shadow-md hover:bg-primary-600 hover:border-primary-600 hover:text-white transition-colors"
                              title={t('chat.edit.editMessage', 'Edit message')}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleCopyToInput(message.content)}
                              className="flex-shrink-0 p-1.5 rounded-full bg-dark-card dark:bg-dark-card bg-light-surface border border-dark-border dark:border-dark-border border-light-border shadow-md hover:bg-primary-600 hover:border-primary-600 hover:text-white transition-colors"
                              title={t('chat.copyToInput', 'Copy to input')}
                            >
                              <ClipboardDocumentIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setRollbackMessageId(message.id);
                                setShowRollbackConfirm(true);
                              }}
                              className="flex-shrink-0 p-1.5 rounded-full bg-dark-card dark:bg-dark-card bg-light-surface border border-dark-border dark:border-dark-border border-light-border shadow-md hover:bg-primary-600 hover:border-primary-600 hover:text-white transition-colors"
                              title={t('chat.rollback.rollbackToHere', 'Roll back to here')}
                            >
                              <ArrowUturnLeftIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      {editingMessageId === message.id ? (
                        <div className="max-w-[75%] flex flex-col gap-2">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full px-4 py-3 rounded-2xl bg-primary-700 text-white border-2 border-primary-400 focus:outline-none resize-y min-h-[60px]"
                            rows={Math.min(8, editContent.split('\n').length + 1)}
                            autoFocus
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 text-sm rounded-lg bg-dark-card dark:bg-dark-card bg-light-surface text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:bg-dark-border dark:hover:bg-dark-border hover:bg-light-border transition-colors"
                            >
                              {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                              onClick={handleSubmitEdit}
                              disabled={!editContent.trim() || isSending}
                              className="px-3 py-1 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
                            >
                              {isSending
                                ? t('chat.edit.saving', 'Saving...')
                                : t('chat.edit.save', 'Save & Send')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`px-4 py-3 max-w-[75%] ${
                            message.role === 'user'
                              ? 'rounded-2xl rounded-br-md'
                              : 'ds-card rounded-2xl rounded-bl-md'
                          }`}
                          style={
                            message.role === 'user'
                              ? {
                                  background: 'var(--ds-user-msg-bg, #9333ea)',
                                  color: 'var(--ds-user-msg-text, #fff)',
                                }
                              : {
                                  background: 'var(--ds-ai-msg-bg, #1a1a1a)',
                                  color: 'var(--ds-ai-msg-text, #fff)',
                                  border: '1px solid var(--ds-ai-msg-border, #2a2a2a)',
                                }
                          }
                        >
                          {message.imageUrl && (
                            <img
                              src={message.imageUrl}
                              alt=""
                              className="max-w-full rounded-lg mb-2 cursor-pointer"
                              style={{ maxHeight: '300px' }}
                              onClick={() => window.open(message.imageUrl, '_blank')}
                            />
                          )}
                          {message.content &&
                            message.content !== '[Image]' &&
                            (() => {
                              const tarotMatch = message.content.match(
                                /\[SUGGEST_TAROT:(single|three-card|celtic-cross)\]/,
                              );
                              const cleanContent = message.content
                                .replace(
                                  /\s*\[SUGGEST_TAROT:(single|three-card|celtic-cross)\]\s*/,
                                  '',
                                )
                                .trim();
                              return (
                                <>
                                  <p className="whitespace-pre-wrap break-words">{cleanContent}</p>
                                  {tarotMatch && (
                                    <button
                                      onClick={() => {
                                        setShowTarotPanel(true);
                                      }}
                                      className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 hover:border-purple-400/50 hover:from-purple-600/30 hover:to-indigo-600/30 transition-all duration-200 group"
                                    >
                                      <span className="text-lg">🔮</span>
                                      <span className="text-sm text-purple-200 group-hover:text-purple-100">
                                        {t(
                                          'tarot.companionSuggests',
                                          'Your companion suggests a reading — Pull cards?',
                                        )}
                                      </span>
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                        </div>
                      )}
                      {/* Action buttons - right of companion messages */}
                      {message.role !== 'user' &&
                        hoveredMessageId === message.id &&
                        !showRollbackConfirm &&
                        !editingMessageId && (
                          <div className="flex items-center gap-1">
                            {/* Regenerate button - only on the last AI message */}
                            {message.id ===
                              messages.filter((m) => m.role === 'assistant').slice(-1)[0]?.id && (
                              <button
                                onClick={handleRegenerate}
                                disabled={isRegenerating || isSending}
                                className="flex-shrink-0 p-1.5 rounded-full bg-dark-card dark:bg-dark-card bg-light-surface border border-dark-border dark:border-dark-border border-light-border shadow-md hover:bg-primary-600 hover:border-primary-600 hover:text-white transition-colors disabled:opacity-50"
                                title={t('chat.regenerate.title', 'Regenerate response')}
                              >
                                <ArrowPathIcon
                                  className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`}
                                />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setRollbackMessageId(message.id);
                                setShowRollbackConfirm(true);
                              }}
                              className="flex-shrink-0 p-1.5 rounded-full bg-dark-card dark:bg-dark-card bg-light-surface border border-dark-border dark:border-dark-border border-light-border shadow-md hover:bg-primary-600 hover:border-primary-600 hover:text-white transition-colors"
                              title={t('chat.rollback.rollbackToHere', 'Roll back to here')}
                            >
                              <ArrowUturnLeftIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                    </div>
                    <div
                      className={`flex ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      } px-4`}
                    >
                      <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                        {new Date(message.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {message.role === 'user' && <span className="ml-1">✓✓</span>}
                        {message.model && user?.isAdmin && (
                          <span className="ml-2 opacity-50">({message.model})</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start mb-2 animate-fade-in">
                    <div className="flex items-center space-x-2">
                      <div className="bg-dark-card dark:bg-dark-card bg-light-surface text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                        <div className="flex items-center space-x-2">
                          <div className="flex space-x-1">
                            <span className="inline-block w-2 h-2 bg-purple-500 rounded-full animate-typing-dot"></span>
                            <span
                              className="inline-block w-2 h-2 bg-purple-500 rounded-full animate-typing-dot"
                              style={{ animationDelay: '0.2s' }}
                            ></span>
                            <span
                              className="inline-block w-2 h-2 bg-purple-500 rounded-full animate-typing-dot"
                              style={{ animationDelay: '0.4s' }}
                            ></span>
                          </div>
                          <span className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary ml-1">
                            {t('chat.isTyping', '{{name}} is typing', { name: companion.name })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="border-t border-dark-border dark:border-dark-border border-light-border px-4 py-3">
            {!isSubscribed && isConnected && (
              <div className="text-xs text-yellow-500 mb-2 text-center">
                {t('chat.connectingToChat', 'Connecting to chat service...')}
              </div>
            )}
            <form onSubmit={handleSendMessage}>
              {/* Image preview */}
              {imagePreview && (
                <div className="mb-2 relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-32 rounded-lg border border-dark-border dark:border-dark-border border-light-border"
                  />
                  <button
                    type="button"
                    onClick={handleClearImage}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-end space-x-2">
                {/* Image upload button */}
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isSending}
                  className="p-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-500 transition-colors disabled:opacity-50"
                  title={t('chat.image.upload', 'Upload image')}
                >
                  <PhotoIcon className="w-6 h-6" />
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => setShowRitualPhrasePanel(true)}
                  className="p-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-purple-400 transition-colors"
                  title={t('ritualPhrases', 'Ritual Phrases')}
                >
                  🕯️
                </button>
                <div className="flex-1 relative">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    placeholder={
                      isSending
                        ? t('chat.sending', 'Sending...')
                        : t('chat.shareThoughts', 'Share your thoughts')
                    }
                    className={`w-full px-4 py-3 ${
                      inputMessage.length > MAX_MESSAGE_LENGTH
                        ? 'border-red-500 focus:ring-red-500'
                        : 'focus:ring-primary-500'
                    } rounded-3xl focus:outline-none focus:ring-2 resize-none`}
                    style={{
                      background: 'var(--ds-bg-card, #1a1a1a)',
                      border: `1px solid ${inputMessage.length > MAX_MESSAGE_LENGTH ? '#ef4444' : 'var(--ds-border, #2a2a2a)'}`,
                      color: 'var(--ds-text-primary, #fff)',
                      maxHeight: '120px',
                      overflow: inputMessage.split('\n').length > 4 ? 'auto' : 'hidden',
                      height: 'auto',
                    }}
                    disabled={isSending}
                    rows={1}
                    ref={(el) => {
                      if (el) {
                        el.style.height = 'auto';
                        el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                      }
                    }}
                  />
                  {inputMessage.length > MAX_MESSAGE_LENGTH * 0.8 && (
                    <div
                      className={`absolute right-3 bottom-1 text-xs ${
                        inputMessage.length > MAX_MESSAGE_LENGTH
                          ? 'text-red-500 font-semibold'
                          : 'text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary'
                      }`}
                    >
                      {inputMessage.length.toLocaleString()}/{MAX_MESSAGE_LENGTH.toLocaleString()}
                    </div>
                  )}
                </div>

                {(inputMessage.trim() || selectedImage) && (
                  <button
                    type="submit"
                    className="p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSending}
                  >
                    <PaperAirplaneSolid className="w-6 h-6" />
                  </button>
                )}
              </div>
            </form>

            {!isConnected && (
              <p className="text-xs text-yellow-500 mt-2 text-center">
                {t('chat.reconnectingToServer', 'Reconnecting to chat server...')}
              </p>
            )}
          </div>

          {/* PWA Install Prompt */}
        </div>

        {/* Usage limit soft wall */}
        {softWall && (
          <SoftWallModal
            isOpen={true}
            onClose={() => setSoftWall(null)}
            type={softWall.type}
            usage={softWall.usage}
          />
        )}

        {/* Rollback confirmation modal */}
        {showRollbackConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
            <div className="bg-dark-card dark:bg-dark-card bg-light-surface rounded-2xl p-6 max-w-sm w-full shadow-xl">
              <h3 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-2">
                {t('chat.rollback.confirmTitle', 'Roll Back Conversation?')}
              </h3>
              <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
                {t(
                  'chat.rollback.confirmMessage',
                  'All messages after this point will be permanently deleted. This cannot be undone.',
                )}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={dismissRollbackMenu}
                  disabled={isRollingBack}
                  className="flex-1 px-4 py-2 text-sm bg-dark-bg dark:bg-dark-bg bg-light-bg text-dark-text dark:text-dark-text text-light-text rounded-lg hover:bg-dark-border dark:hover:bg-dark-border hover:bg-light-border transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={executeRollback}
                  disabled={isRollingBack}
                  className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isRollingBack
                    ? t('chat.rollback.rollingBack', 'Rolling back...')
                    : t('chat.rollback.confirm', 'Roll Back')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Context Info Panel */}
      {conversation?.id && (
        <ContextInfoPanel
          isOpen={showContextPanel}
          onClose={() => setShowContextPanel(false)}
          conversationId={conversation.id}
        />
      )}

      {/* Tarot Pull Panel */}
      <TarotPullPanel
        isOpen={showTarotPanel}
        onClose={() => setShowTarotPanel(false)}
        onSendToChat={(text) => {
          setInputMessage(text);
          setShowTarotPanel(false);
        }}
      />
      {companion && 'id' in companion && companion.id && (
        <ConversationDashboard
          isOpen={showConversationDashboard}
          onClose={() => setShowConversationDashboard(false)}
          companionId={companion.id}
          currentConversationId={conversation?.id}
          onSelectConversation={(id) => {
            navigate(`/chat?conversationId=${id}`);
            window.location.reload();
          }}
          onNewConversation={() => {
            navigate(`/chat?companionId=${companion.id}&new=true`);
            window.location.reload();
          }}
        />
      )}

      {showRitualPhrasePanel && companion && 'id' in companion && companion.id && (
        <RitualPhrasePanel
          companionId={companion.id}
          onInsert={(phrase) => {
            setInputMessage((prev) => (prev ? prev + '\n' + phrase : phrase));
            setShowRitualPhrasePanel(false);
          }}
          onClose={() => setShowRitualPhrasePanel(false)}
        />
      )}
    </MobileLayout>
  );
};

export default Chat;
